import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';
import AnalyticsFilterBar from './AnalyticsFilterBar';
import { toQuery, useAnalyticsFilters } from './filters';
import { BarChart, LineChart, StackedBar } from './charts';
import { deltaPct, fmtDate, fmtHours, fmtMin, fmtNum, fmtPct } from './format';

// "Przegląd okresu" (spec 7.2): one table instead of separate day / shift / week / month
// screens — the range and comparison come from the filter bar.

export interface Kpi {
  prodMin: number; ssMin: number; ssCnt: number; otMin: number; otCnt: number;
  downMin: number; downCnt: number; noDataMin: number; microCnt: number; microMin: number;
  availMin: number; availability: number | null; avgSpeed: number | null; tgtSpeed: number | null;
  performance: number | null; pcs: number; kgNet: number; kgFlow: number; kgScrap: number; cycles: number;
  kgPerH: number | null; potentialKg: number | null; utilization: number | null;
  stopsPerProdH: number | null; avgDownMin: number | null; hasTelemetry: boolean;
}

interface OverviewData {
  from: string;
  to: string;
  kpi: Kpi;
  previous: Kpi | null;
  series: (Kpi & { date: string })[];
  lines: (Kpi & { id: number; name: string; external_code: string | null })[];
  thresholds: { min_speed_m_min: number; micro_stop_seconds: number; short_stop_seconds: number; stale_seconds: number };
}

const Overview: React.FC = () => {
  const { t } = useLanguage();
  const [filters, setFilters] = useAnalyticsFilters();
  const [data, setData] = useState<OverviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get('/analytics/overview', { params: toQuery(filters) })
      .then(res => { if (!cancelled) { setData(res.data); setError(null); } })
      .catch(err => { if (!cancelled) setError(err?.response?.data?.message ?? t('analytics.loadError')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [filters, t]);

  const k = data?.kpi;
  const p = data?.previous ?? null;
  const tile = (label: string, value: string, opts: { sub?: string; cur?: number | null; prev?: number | null; invert?: boolean; bar?: number | null; warn?: boolean } = {}) => (
    <Tile label={label} value={value} sub={opts.sub} delta={filters.compare && p ? deltaPct(opts.cur, opts.prev) : null} invert={opts.invert} bar={opts.bar} warn={opts.warn} vsLabel={t('analytics.vsPrev')} />
  );

  return (
    <div style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: '#1e293b' }}>📈 {t('analytics.overviewTitle')}</h1>
        <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: 14 }}>{t('analytics.overviewSubtitle')}</p>
      </div>

      <AnalyticsFilterBar filters={filters} onChange={setFilters} />

      {error && <div style={alert}>{error}</div>}
      {loading && !data && <div style={{ color: '#94a3b8', padding: 32, textAlign: 'center' }}>{t('common.loading')}</div>}

      {k && (
        <div style={{ opacity: loading ? 0.6 : 1, transition: 'opacity 0.15s' }}>
          <div style={grid}>
            {tile(t('analytics.kpiNetKg'), `${fmtNum(k.kgNet, 1)} kg`, { sub: t('analytics.kpiNetKgSub'), cur: k.kgNet, prev: p?.kgNet })}
            {tile(t('analytics.kpiSpools'), `${fmtNum(k.pcs)} ${t('analytics.pcs')}`, { cur: k.pcs, prev: p?.pcs })}
            {tile(t('analytics.kpiFlowKg'), `${fmtNum(k.kgFlow, 1)} kg`, { sub: t('analytics.kpiFlowKgSub', { count: fmtNum(k.cycles) }), cur: k.kgFlow, prev: p?.kgFlow })}
            {tile(t('analytics.kpiKgPerH'), k.kgPerH === null ? '—' : `${fmtNum(k.kgPerH, 1)} kg/h`, { sub: t('analytics.kpiKgPerHSub'), cur: k.kgPerH, prev: p?.kgPerH })}
            {tile(t('analytics.kpiAvgSpeed'), k.avgSpeed === null ? '—' : `${fmtNum(k.avgSpeed, 1)} m/min`, { sub: k.tgtSpeed ? t('analytics.target', { value: fmtNum(k.tgtSpeed, 0) }) : undefined, cur: k.avgSpeed, prev: p?.avgSpeed })}
            {tile(t('analytics.kpiAvailability'), fmtPct(k.availability), { sub: t('analytics.kpiAvailabilitySub'), cur: k.availability, prev: p?.availability, bar: k.availability })}
            {tile(t('analytics.kpiPerformance'), fmtPct(k.performance), { sub: k.performance === null ? t('analytics.noTargets') : undefined, cur: k.performance, prev: p?.performance, bar: k.performance })}
            {tile(t('analytics.kpiUtilization'), fmtPct(k.utilization), { sub: k.utilization === null ? t('analytics.noTargets') : t('analytics.potential', { value: fmtNum(k.potentialKg, 0) }), cur: k.utilization, prev: p?.utilization, bar: k.utilization })}
            {tile(t('analytics.kpiDowntime'), fmtHours(k.downMin), { sub: t('analytics.kpiDowntimeSub', { count: fmtNum(k.downCnt), avg: fmtMin(k.avgDownMin) }), cur: k.downMin, prev: p?.downMin, invert: true })}
            {tile(t('analytics.kpiShortStops'), fmtMin(k.ssMin), { sub: t('analytics.kpiShortStopsSub', { count: fmtNum(k.ssCnt), rate: fmtNum(k.stopsPerProdH, 2) }), cur: k.ssCnt, prev: p?.ssCnt, invert: true })}
            {tile(t('analytics.kpiMicroStops'), fmtMin(k.microMin), { sub: t('analytics.kpiMicroStopsSub', { count: fmtNum(k.microCnt), sec: String(data!.thresholds.micro_stop_seconds) }), cur: k.microCnt, prev: p?.microCnt, invert: true })}
            {tile(t('analytics.kpiNoData'), fmtHours(k.noDataMin), { sub: t('analytics.kpiNoDataSub'), cur: k.noDataMin, prev: p?.noDataMin, invert: true, warn: k.noDataMin > k.availMin * 0.1 })}
          </div>

          <div style={card}>
            <div style={cardHead}><b>{t('analytics.timeStructure')}</b><span style={muted}>{t('analytics.timeStructureSub', { hours: fmtNum(k.availMin / 60, 1) })}</span></div>
            <StackedBar
              format={fmtMin}
              parts={[
                { label: t('analytics.stProd'), value: k.prodMin, color: '#2f80ed' },
                { label: t('analytics.stShort'), value: k.ssMin, color: '#e8590c' },
                { label: t('analytics.stDown'), value: k.otMin, color: '#6b7280' },
                { label: t('analytics.stNoData'), value: k.noDataMin, color: '#d1d5db' },
              ]}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 16, marginBottom: 16 }}>
            <div style={card}>
              <div style={cardHead}><b>{t('analytics.chartDailyKg')}</b><span style={muted}>{t('analytics.chartDailyKgSub')}</span></div>
              <BarChart data={data!.series.map(s => ({ label: fmtDate(s.date), value: s.kgNet }))} format={v => fmtNum(v, 0)} />
            </div>
            <div style={card}>
              <div style={cardHead}><b>{t('analytics.chartRatios')}</b><span style={muted}>{t('analytics.chartRatiosSub')}</span></div>
              <LineChart
                max={1}
                format={v => `${Math.round(v * 100)}%`}
                series={[
                  { name: 'Availability', color: '#0f9d58', points: data!.series.map(s => ({ label: fmtDate(s.date), value: s.availability })) },
                  { name: 'Performance', color: '#e8590c', points: data!.series.map(s => ({ label: fmtDate(s.date), value: s.performance })) },
                ]}
              />
            </div>
          </div>

          <div style={card}>
            <div style={cardHead}><b>{t('analytics.linesInPeriod')}</b><span style={muted}>{t('analytics.linesInPeriodSub')}</span></div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f8f9fa' }}>
                    <th style={th}>{t('analytics.colLine')}</th>
                    <th style={thR}>{t('analytics.colProd')}</th>
                    <th style={thR}>{t('analytics.colDown')}</th>
                    <th style={thR}>{t('analytics.colShort')}</th>
                    <th style={thR}>{t('analytics.colNoData')}</th>
                    <th style={thR}>Availability</th>
                    <th style={thR}>{t('analytics.colSpeed')}</th>
                    <th style={thR}>{t('analytics.colTarget')}</th>
                    <th style={thR}>Performance</th>
                    <th style={thR}>{t('analytics.colPcs')}</th>
                    <th style={thR}>{t('analytics.colKg')}</th>
                    <th style={thR}>{t('analytics.colKgFlow')}</th>
                    <th style={thR}>kg/h</th>
                  </tr>
                </thead>
                <tbody>
                  {data!.lines.map(l => (
                    <tr key={l.id} onClick={() => setFilters({ lineId: filters.lineId === String(l.id) ? '' : String(l.id) })} style={{ cursor: 'pointer', background: filters.lineId === String(l.id) ? '#eef2ff' : undefined }}>
                      <td style={{ ...td, fontWeight: 700 }}>
                        {l.name}
                        {l.external_code && <span style={{ ...muted, marginLeft: 6, fontWeight: 400 }}>{l.external_code}</span>}
                        {!l.hasTelemetry && <span style={{ ...muted, marginLeft: 6, fontWeight: 400 }}>· {t('analytics.noTelemetry')}</span>}
                      </td>
                      <td style={tdR}>{l.hasTelemetry ? fmtMin(l.prodMin) : '—'}</td>
                      <td style={tdR}>{l.hasTelemetry ? <>{fmtMin(l.otMin)} <span style={muted}>({l.otCnt})</span></> : '—'}</td>
                      <td style={tdR}>{l.hasTelemetry ? <>{fmtMin(l.ssMin)} <span style={muted}>({l.ssCnt})</span></> : '—'}</td>
                      <td style={{ ...tdR, color: l.noDataMin > 0 ? '#d97706' : undefined }}>{l.hasTelemetry ? fmtMin(l.noDataMin) : '—'}</td>
                      <td style={{ ...tdR, fontWeight: 700 }}>{fmtPct(l.availability)}</td>
                      <td style={tdR}>{fmtNum(l.avgSpeed, 1)}</td>
                      <td style={tdR}>{fmtNum(l.tgtSpeed, 0)}</td>
                      <td style={{ ...tdR, fontWeight: 700, color: l.performance === null ? undefined : l.performance >= 0.9 ? '#059669' : l.performance >= 0.75 ? '#d97706' : '#dc2626' }}>{fmtPct(l.performance)}</td>
                      <td style={tdR}>{fmtNum(l.pcs)}</td>
                      <td style={{ ...tdR, fontWeight: 700 }}>{fmtNum(l.kgNet, 1)}</td>
                      <td style={tdR}>{fmtNum(l.kgFlow, 1)}</td>
                      <td style={tdR}>{fmtNum(l.kgPerH, 1)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ background: '#f8f9fa', fontWeight: 700 }}>
                    <td style={td}>{t('analytics.total')}</td>
                    <td style={tdR}>{fmtMin(k.prodMin)}</td>
                    <td style={tdR}>{fmtMin(k.otMin)} <span style={muted}>({k.otCnt})</span></td>
                    <td style={tdR}>{fmtMin(k.ssMin)} <span style={muted}>({k.ssCnt})</span></td>
                    <td style={tdR}>{fmtMin(k.noDataMin)}</td>
                    <td style={tdR}>{fmtPct(k.availability)}</td>
                    <td style={tdR}>{fmtNum(k.avgSpeed, 1)}</td>
                    <td style={tdR}>{fmtNum(k.tgtSpeed, 0)}</td>
                    <td style={tdR}>{fmtPct(k.performance)}</td>
                    <td style={tdR}>{fmtNum(k.pcs)}</td>
                    <td style={tdR}>{fmtNum(k.kgNet, 1)}</td>
                    <td style={tdR}>{fmtNum(k.kgFlow, 1)}</td>
                    <td style={tdR}>{fmtNum(k.kgPerH, 1)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          <p style={{ ...muted, marginTop: 12 }}>
            {t('analytics.definitionsNote', { min: String(data!.thresholds.min_speed_m_min), short: String(Math.round(data!.thresholds.short_stop_seconds / 60)), stale: String(data!.thresholds.stale_seconds) })}
          </p>
        </div>
      )}
    </div>
  );
};

export const Tile: React.FC<{ label: string; value: string; sub?: string; delta: number | null; invert?: boolean; bar?: number | null; warn?: boolean; vsLabel: string }> = ({ label, value, sub, delta, invert, bar, warn, vsLabel }) => {
  const good = delta === null ? null : (invert ? delta < 0 : delta > 0);
  return (
    <div style={{ background: '#fff', border: `1px solid ${warn ? '#fcd34d' : '#e2e8f0'}`, borderRadius: 10, padding: '14px 16px', minWidth: 0 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', letterSpacing: 0.5, textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 800, color: '#1e293b', fontVariantNumeric: 'tabular-nums', marginTop: 2, whiteSpace: 'nowrap' }}>{value}</div>
      {delta !== null && (
        <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
          <span style={{ color: good ? '#059669' : '#dc2626', fontWeight: 700 }}>{delta >= 0 ? '▲' : '▼'} {Math.abs(delta * 100).toFixed(1)}%</span> {vsLabel}
        </div>
      )}
      {sub && <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{sub}</div>}
      {bar !== null && bar !== undefined && (
        <div style={{ height: 5, background: '#e2e8f0', borderRadius: 3, marginTop: 8, overflow: 'hidden' }}>
          <div style={{ width: `${Math.min(100, bar * 100)}%`, height: '100%', background: '#667eea' }} />
        </div>
      )}
    </div>
  );
};

export const grid: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12, marginBottom: 16 };
export const card: React.CSSProperties = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '16px 18px', marginBottom: 16 };
export const cardHead: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, marginBottom: 12, flexWrap: 'wrap', fontSize: 15, color: '#1e293b' };
export const muted: React.CSSProperties = { fontSize: 12, color: '#94a3b8' };
export const alert: React.CSSProperties = { background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#991b1b' };
export const th: React.CSSProperties = { padding: '10px 12px', textAlign: 'left', fontSize: 11, color: '#6c757d', borderBottom: '2px solid #dee2e6', whiteSpace: 'nowrap', letterSpacing: 0.5 };
export const thR: React.CSSProperties = { ...th, textAlign: 'right' };
export const td: React.CSSProperties = { padding: '9px 12px', borderBottom: '1px solid #f0f0f0', verticalAlign: 'middle', whiteSpace: 'nowrap' };
export const tdR: React.CSSProperties = { ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };

export default Overview;
